/**
 * AvailabilityPage — Availability & Cash Truth Layer.
 * Shows the liquidity waterfall, 13-week availability trend, and outstanding
 * uncleared items (checks / ACH).
 */

import React from 'react';
import { Card, CardContent } from '../../components/ui';
import { useAvailability } from './api';
import { formatCurrency } from './utils';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

export default function AvailabilityPage() {
  const { data, isLoading } = useAvailability();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-gray-400">Loading availability data...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500 dark:text-gray-400">
          No availability data. Generate a forecast first.
        </CardContent>
      </Card>
    );
  }

  const { current, history, uncleared_items } = data;
  const totalUnclearedAmount = uncleared_items.reduce((s, i) => s + i.amount, 0);

  const steps = [
    { label: 'Book Cash', value: current.book_cash, color: 'bg-blue-500', negative: false },
    { label: 'Bank Cash', value: current.bank_cash, color: 'bg-blue-400', negative: false },
    { label: 'Restricted', value: current.restricted_cash, color: 'bg-red-400', negative: true },
    { label: 'Outstanding Checks', value: current.outstanding_checks, color: 'bg-red-400', negative: true },
    { label: 'True Operating', value: current.true_operating_cash, color: 'bg-emerald-500', negative: false },
    { label: 'Available Credit', value: current.available_credit, color: 'bg-indigo-400', negative: false },
    { label: 'Total Availability', value: current.total_availability, color: 'bg-emerald-600', negative: false },
  ];

  const chartData = [...history].reverse().map((h, i) => ({
    week: `W${i + 1}`,
    total_availability: h.total_availability,
    true_operating: h.true_operating_cash,
    covenant_min: 12_000_000,
  }));

  return (
    <div className="space-y-6 mt-2">
      {/* Hero: Total Availability */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Total Availability
              </p>
              <p className="text-4xl font-bold text-charcoal dark:text-gray-100">
                {formatCurrency(current.total_availability, true)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                as of {current.as_of_date}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Covenant Headroom
              </p>
              <p
                className={`text-2xl font-bold ${
                  (current.covenant_headroom ?? 0) > 0
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {formatCurrency(current.covenant_headroom ?? 0, true)}
              </p>
            </div>
          </div>

          {/* Waterfall */}
          <div className="grid grid-cols-7 gap-2">
            {steps.map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 leading-tight">
                  {s.label}
                </p>
                <p
                  className={`text-sm font-semibold ${
                    s.negative
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-charcoal dark:text-gray-200'
                  }`}
                >
                  {s.negative ? '−' : ''}
                  {formatCurrency(Math.abs(s.value), true)}
                </p>
                <div
                  className={`h-1.5 rounded-full mt-1 ${
                    s.negative ? 'bg-red-400' : s.color
                  }`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Trend Chart */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold text-charcoal dark:text-gray-200 mb-4">
            13-Week Availability Trend
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="week" tick={{ fontSize: 12 }} />
              <YAxis
                tickFormatter={(v: number) => `$${(v / 1e6).toFixed(0)}M`}
                tick={{ fontSize: 12 }}
              />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <ReferenceLine
                y={12_000_000}
                stroke="#ef4444"
                strokeDasharray="5 5"
                label={{ value: 'Covenant Min', position: 'right', fontSize: 11 }}
              />
              <Area
                type="monotone"
                dataKey="total_availability"
                stroke="#059669"
                fill="#059669"
                fillOpacity={0.1}
                name="Total Availability"
              />
              <Area
                type="monotone"
                dataKey="true_operating"
                stroke="#6366f1"
                fill="#6366f1"
                fillOpacity={0.1}
                name="True Operating Cash"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Uncleared Items */}
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-200">
              Outstanding Uncleared Items
            </h3>
            <span className="text-sm font-mono text-gray-500 dark:text-gray-400">
              {uncleared_items.length} items totaling {formatCurrency(totalUnclearedAmount)}
            </span>
          </div>
          {uncleared_items.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-6">
              No outstanding uncleared items.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-dark-border">
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reference
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payee
                    </th>
                    <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Issue Date
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expected Clear
                    </th>
                    <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                  {[...uncleared_items]
                    .sort((a, b) => b.amount - a.amount)
                    .map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-gray-50 dark:hover:bg-dark-hover"
                      >
                        <td className="py-2 px-3 font-mono text-xs text-charcoal dark:text-gray-200">
                          {item.reference_number ?? '—'}
                        </td>
                        <td className="py-2 px-3">
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                            {item.adjustment_type}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-charcoal dark:text-gray-200">
                          {item.payee ?? '—'}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-charcoal dark:text-gray-200">
                          {formatCurrency(item.amount)}
                        </td>
                        <td className="py-2 px-3 text-gray-500 dark:text-gray-400">
                          {item.issue_date ?? '—'}
                        </td>
                        <td className="py-2 px-3 text-gray-500 dark:text-gray-400">
                          {item.expected_clear_date ?? '—'}
                        </td>
                        <td className="py-2 px-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${
                              item.status === 'outstanding'
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                            }`}
                          >
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
